'use strict';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { DateTime } from 'luxon'

import DatePicker from '@/Form/V2/DatePicker'
import Select     from '@/Form/V2/Select'
import Input      from '@/Form/V2/Input'

import FrontBillRow from '@/Lists/Rows/FrontBillRow'
import Dashboard    from '@/Lists/Dashboard'

import PageWrapper from '@Layout/PageWrapper'
import Layout      from '@Layout/ECFront'

import HolidaysMessage from '@/HolidaysMessage'

import Loader from '@Pure/Loader'

import { bill_payment_status } from '@Config/options'

import { num, formatNum } from '@root/utility'

const { app } = window;

const header =
[
	{ label: 'Date', name: 'date', sortable: true },
	{ label: 'N° de commande', name: 'order_number', sortable: false },
	{ label: 'Montant', name: 'total_price', sortable: false },
	{ label: 'Réglé', name: 'paid_amount', sortable: false },
	{ label: 'Restant', name: 'remaining_unpaid', sortable: false },
	{ label: 'Télécharger la facture', name: 'number', sortable: true }
];

const DASHBOARD = 'FrontBills';

const fields = header.map(h => h.name);

const fields_with_timeout = ['start', 'end', 'number', 'order_number'];

const storage_filters =
{
	start: DateTime.now().plus({ days: -75 }).startOf('month').toISODate(),

	end: DateTime.now().endOf('month').toISODate(),

	order_number: window.localStorage.getItem(DASHBOARD + '.order_number') || '',
	number: window.localStorage.getItem(DASHBOARD + '.number') || '',

	payment_status: window.localStorage.getItem(DASHBOARD + '.payment_status') || ''
};

const payment_status_options =
[
	{ value: 'all', label: 'Tout' }
]
.concat(bill_payment_status);

function getSessionRoles ()
{
	return [app.state?.session?.permissions?.title].filter(role => role);
}

export default PageWrapper(function ECBillsPage (props)
{
	const [roles, setRoles] = useState(getSessionRoles());
	const [stats, setStats] = useState();

	const search_timeout = useRef();

	const [instant_filters, setInstantFilters] = useState({ ...storage_filters });
	const [delayed_filters, setDelayedFilters] = useState({ ...storage_filters });

	useEffect(function ()
	{
		app.push(
		{
			event: 'sessionChange',
			cb: handleSessionChange
		});

		const roles = getSessionRoles();

		if (roles.length) setRoles(roles);

		return () => app.delete(
		{
			event: 'sessionChange',
			cb: handleSessionChange
		});

		function handleSessionChange ()
		{
			setRoles(getSessionRoles());
		}
	},
	[]);

	const handleFetched = useCallback(function ({ response })
	{
		setStats(
		{
			remaining_unpaid: num(response.remaining_unpaid)
		});
	},
	[]);

	const handleChange = useCallback(function ({ value, e, name })
	{
		if (!name) return;

		if (e === null && fields_with_timeout.includes(name) === false) return;

	// Prevent on mount trigger
		if (e === null && fields_with_timeout.includes(name)) return;

		window.localStorage.setItem(DASHBOARD + '.' + name, value);

		setInstantFilters(function (filters)
		{
			return { ...filters, [name]: value };
		});

		if (fields_with_timeout.includes(name) === false) return;

		if (search_timeout.current)
		{
			window.clearTimeout(search_timeout.current);
		}

		search_timeout.current = window.setTimeout(function ()
		{
			search_timeout.current = undefined;

			if (['start', 'end'].includes(name))
			{
				if (!DateTime.fromISO(value).isValid) return;
			}

			setDelayedFilters(function (filters)
			{
				return { ...filters, [name]: value };
			});
		},
		320);
	},
	[]);

	const handleNativeChange = useCallback(function (e)
	{
		handleChange({ value: e?.target?.value, name: e?.target?.name, e });
	},
	[]);

	const filters = useMemo(function ()
	{
		const filters =
		{
			start: delayed_filters.start,
			end: delayed_filters.end,

			order_number: delayed_filters.order_number,
			number: delayed_filters.number,

			payment_status: instant_filters.payment_status
		};

		return filters;
	},
	[instant_filters, delayed_filters]);

	const fetching = !roles.length ?
	(
		<Loader className="c-loader" bemit={ ['white', 'bg-carbon'] } />
	)
	: undefined;

	const page_title = roles.includes('EC')
	? 'Factures du CSE'
	: 'Mes factures';

	const stats_table = roles.includes('EC') && stats ?
	(
		<table className="c-table-stats u-margin-bottom-20">

			<tbody>
				<tr className="c-table-stats__row">

					<td className="c-table-stats__cell Stats-remaining_unpaid">
						Restant dû à Synergies
					</td>

					<td className={ 'c-table-stats__cell Stats-remaining_unpaid' +
						(stats.remaining_unpaid > 1 ? ' is-red' : ' is-green') }>
						{ formatNum(stats.remaining_unpaid, '0,0[.]00') }
						{ ' €' }
					</td>

				</tr>
			</tbody>

		</table>
	)
	: undefined;

	return (
		<Layout page_title={ page_title } history={ props.history }>

			<div className="page-content">

				<HolidaysMessage />

				<div className="o-content u-positioned
					u-full-width u-flex u-content-center">

					<div className="table-page-container" style={ { width: 1050 } }>

						{ stats_table }

						<div className="front-filters-container u-margin-bottom-20">
							<DatePicker name="start" label="Du"
								onChange={ handleChange }
								value={ instant_filters.start }
								bemit={ ['fixed-size'] }
							/>

							<DatePicker name="end" label="Au"
								onChange={ handleChange }
								value={ instant_filters.end }
								bemit={ ['fixed-size'] }
							/>

							<Select name="payment_status"
								onChange={ handleNativeChange }
								value={ instant_filters.payment_status }
								label="Réglé"
								bemit={ ['fixed-payment_status'] }
								options={ payment_status_options }
							/>

							<Input name="order_number"
								onChange={ handleNativeChange }
								value={ instant_filters.order_number }
								label="N° de commande"
								bemit={ ['20%'] }
							/>

							<Input name="number"
								onChange={ handleNativeChange }
								value={ instant_filters.number }
								label="N° de Facture"
								bemit={ ['20%'] }
							/>
						</div>

						{
							fetching ? fetching :
							(
								<Dashboard model="Bill" items-per-page={ 50 }
									url="/front-fetch/bills"
									header={ header } fields={ fields }
									order="DESC" order-by="date"
									component={ FrontBillRow }
									bemit={ ['front-CSE-bills'] }
									onFetched={ handleFetched }
									pagination={ false }
									filters={ filters }
								/>
							)
						}

					</div>

				</div>

			</div>

		</Layout>
	);
});